/**
 * @module Components.Selectbox
 *
 */
import React, { useRef, useState, useMemo } from "react";
import { defineMessages } from "react-intl";
import { components } from "react-select";
import { OptionProps } from "react-select/src/components/Option";
import { MultiValueProps } from "react-select/src/components/MultiValue";
import { NoticeProps } from "react-select/src/components/Menu";
import { useLoc, useStoreActions } from "app/utils/hooks";
import { AuthService } from "app/services";
import SelectBox, {
  StateManager,
  SelectProps,
  OptionsType,
  DefaultOption,
} from "app/components/inputs/SelectBox/Base";
import { uuid } from "app/utils/uuid";
import { cls } from "app/utils";
import { UserModel } from "app/models";
import { fetchConnections } from "app/actions/connection";
import { CreateNewForm } from "./helpers";
import { Loc } from "app/components/helpers";
import UserAvatar from "app/components/UserAvatar";
import { findJack, createMemberJack } from "app/utils/appcues";
import Styles from "./styles.module.scss";

const Auth: any = AuthService;

export interface UserOption extends DefaultOption {
  model: UserModel;
}

interface ComponentOpts {
  isCreatable: boolean;
  selectState: React.MutableRefObject<StateManager<UserOption>>;
  noOptionsMessage: (value: { inputValue: string }) => string;
  onSaveNewOption: (value: UserOption) => void;
  addAthleteBtn: boolean;
}

interface PropsDef extends SelectProps<UserOption> {
  value?: any; // UserModel[];
  defaultValue?: any; // UserModel[];
  excludeOptions?: UserModel[];
  hideSelected?: boolean;
  includeJack?: boolean;
}

const normalizeValue = (val?: UserModel[] | null): UserOption[] | null => {
  if (val == null) {
    return null;
  }

  if (Array.isArray(val)) {
    return val.map(createUserOption);
  } else {
    return [createUserOption(val)];
  }
};

export const customButtonOption = {
  value: "add-new-athlete-btn",
  label: "Add a New Athlete",
  className: "custom-button-option",
};

/**
 * @class SpaceUserSelect
 *
 */
export default function SpaceUserSelect(props: PropsDef) {
  const {
    className,
    value,
    defaultValue,
    isCreatable,
    excludeOptions,
    noOptionsMessage,
    hideSelected,
    includeJack,
    addAthleteBtn = false,
    ...rest
  } = props;

  const { connection } = useStoreActions({ connection: fetchConnections });
  const selectState = useRef<StateManager<UserOption> | null>(null);
  const { formatMessage } = useLoc();
  const [__options, setOptions] = useState<OptionsType<UserOption>>([]);
  const [showNoOptionsForm, setShowNoOptionsForm] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // load connections from api

  const buildOptions = (): void => {
    if (connection.data != null && !showForm) {
      const exclude = excludeOptions || [];

      let users: UserModel[] = connection.data;
      if (exclude.length) {
        users = users.filter((user: UserModel) => {
          return exclude.find((ex: UserModel) => ex.id === user.id) == null;
        });
      }
      if (includeJack && Auth.user && Auth.user.kind === "coach") {
        if (findJack(users, "email") == null) {
          users.unshift(createMemberJack());
        }
      }
      if (isCreatable) {
        const newOptions = users.map(createUserOption);
        newOptions.unshift(customButtonOption as any);
        setOptions(newOptions);
      }
    } else {
      setOptions([]);
    }
  };

  useMemo(() => {
    buildOptions();
  }, [connection.data, excludeOptions, Auth.user, includeJack, showForm]);

  const onSaveNewOption = (value: UserOption) => {
    if (selectState.current != null) {
      selectState.current.createOption([value]);
    }
  };

  const handleClose = () => {
    setShowNoOptionsForm(false);
    setShowForm(false);
  };

  const comps = createComponents(
    {
      isCreatable,
      selectState,
      onSaveNewOption,
      noOptionsMessage,
      addAthleteBtn,
    },
    showNoOptionsForm,
    showForm,
    setShowNoOptionsForm,
    setShowForm
  );

  return (
    <SelectBox
      ref={selectState}
      className={cls("user-select", className)}
      placeholder={formatMessage(LOCALES.placeholder)}
      controlShouldRenderValue={!hideSelected}
      isClearable={true}
      isCreatable={false}
      isMulti={true}
      onMenuClose={handleClose}
      options={__options}
      value={normalizeValue(value)}
      defaultValue={normalizeValue(defaultValue)}
      components={comps}
      {...rest}
    />
  );
}

function createUserOption(user: UserModel): UserOption {
  return {
    value: user.id || uuid(),
    label: `${user.displayName} ${user.email}`,
    model: user,
  };
}

interface AddNewProps {
  label: string;
  showTitle?: boolean;
  onClick: (event: React.FormEvent<HTMLButtonElement>) => void;
}

export function AddNew(props: AddNewProps) {
  const title = (
    <h3 className="needsclick">
      <Loc value={LOCALES.noOptions} />
    </h3>
  );
  return (
    <div
      className={cls("add-new-handle", Styles.noOptionsButton, "needsclick")}
      contentEditable={false}
    >
      {props.showTitle ? title : ""}
      <button className="btn btn-primary needsclick" onClick={props.onClick}>
        {props.label}
      </button>
    </div>
  );
}

function createComponents(
  {
    isCreatable,
    selectState,
    onSaveNewOption,
    noOptionsMessage,
    addAthleteBtn,
  }: ComponentOpts,
  showNoOptionsForm: boolean,
  showForm: boolean,
  setShowNoOptionsForm: (v: boolean) => void,
  setShowForm: (v: boolean) => void
) {
  function NoOptionsMessage(props: NoticeProps<UserOption>) {
    const { formatMessage } = useLoc();
    const inputValue =
      selectState.current && selectState.current.getInputValue();

    const createNewOption = (event: React.FormEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setShowNoOptionsForm(true);
      return false;
    };

    const formatCreateLabel = () => {
      if (noOptionsMessage != null) {
        return noOptionsMessage({ inputValue });
      }
      return formatMessage(LOCALES.noOptsMess);
    };

    const saveNewOption = (user: UserModel) => {
      if (onSaveNewOption != null) {
        onSaveNewOption(createUserOption(user));
      }
      setShowNoOptionsForm(false);
      setShowForm(false);
    };

    if (isCreatable) {
      const rest = { ...props };
      delete rest.children;

      if (showForm) {
        return (
          <>
            <components.NoOptionsMessage {...rest}>
              <CreateNewForm
                getLabel={formatCreateLabel}
                onDone={saveNewOption}
                onCancel={() => setShowForm(false)}
                inputValue={inputValue}
              />
            </components.NoOptionsMessage>
          </>
        );
      }

      if (showNoOptionsForm) {
        return (
          <>
            <components.NoOptionsMessage {...rest}>
              <CreateNewForm
                getLabel={formatCreateLabel}
                onDone={saveNewOption}
                onCancel={() => setShowNoOptionsForm(false)}
                inputValue={inputValue}
              />
            </components.NoOptionsMessage>
          </>
        );
      }
      return (
        <components.NoOptionsMessage {...rest}>
          <AddNew
            showTitle={true}
            label={formatCreateLabel()}
            onClick={createNewOption}
          />
        </components.NoOptionsMessage>
      );
    }
    return <components.NoOptionsMessage {...props} />;
  }

  function Option(props: OptionProps<UserOption>) {
    const rest = { ...props };
    delete rest.children;

    const { data } = props;

    const createNewOption = (event: React.FormEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setShowForm(true);
      setShowNoOptionsForm(false);
    };

    if (data.value === customButtonOption.value) {
      return (
        !showForm &&
        addAthleteBtn && (
          <div
            className={cls("menu-item needsclick", Styles.custom_option_btn)}
            id={data.value}
          >
            <button
              className="btn btn-primary needsclick"
              onClick={createNewOption}
            >
              {data.label}
            </button>
          </div>
        )
      );
    }

    const user = props.data.model;
    if (user == null) {
      return <components.Option {...rest}>{props.children}</components.Option>;
    }

    return (
      <components.Option {...rest}>
        <div className="menu-item needsclick" id={user.id}>
          <div className="profile-block user-select-user profile-block-default needsclick">
            <UserAvatar
              className="avatar needsclick"
              userName={user.name}
              srcName={user.avatar as string}
            />
            <div className="profile-block-details needsclick">
              <div className="profile-block-title needsclick">
                {user.displayName}
              </div>
              <div className="profile-block-summary needsclick">
                {user.email || user.phoneNormalized}
              </div>
            </div>
          </div>
        </div>
      </components.Option>
    );
  }

  function MultiValueLabel(props: MultiValueProps<UserOption>) {
    const rest = { ...props };
    delete rest.children;

    const user = props.data.model;
    return (
      <div className={cls(Styles.selectedUser, "needsclick")} key={user.id}>
        <components.MultiValueLabel {...rest}>
          <UserAvatar
            className="avatar needsclick"
            srcName={user.avatar as string}
          />
          <span className="needsclick">{user.displayName}</span>
        </components.MultiValueLabel>
      </div>
    );
  }

  function Menu(props: any) {
    const { onMouseDown } = props.innerProps;
    props.innerProps.onMouseDown = (evt: React.MouseEvent<HTMLDivElement>) => {
      if (evt.button !== 0) {
        return evt;
      }

      let isButtonClick: boolean = false;
      if (evt.relatedTarget != null) {
        isButtonClick = /btn/.test(
          (evt.relatedTarget as HTMLElement).className
        );
      }

      if (
        evt.currentTarget.contains(evt.target as HTMLElement) &&
        (showNoOptionsForm || showForm || isButtonClick)
      ) {
        return evt;
      }

      return onMouseDown(evt);
    };

    props.innerProps.contentEditable = false;

    return <components.Menu {...props} />;
  }

  function Input({ onBlur, ...props }: any) {
    props.onBlur = (evt: React.MouseEvent<HTMLDivElement>): any => {
      const inputValue =
        selectState.current && selectState.current.getInputValue();

      let isButtonClick: boolean = false;
      if (evt.relatedTarget != null) {
        isButtonClick = /btn/.test(
          (evt.relatedTarget as HTMLElement).className
        );
      }

      if (
        inputValue != null &&
        inputValue.length &&
        (showForm || isButtonClick)
      ) {
        evt.preventDefault();
        evt.stopPropagation();
        return false;
      }
    };
    return <components.Input {...props} />;
  }

  return { Option, NoOptionsMessage, MultiValueLabel, Input, Menu };
}

const LOCALES = defineMessages({
  placeholder: {
    id: "inputs.user-typeahead.placeholder",
    defaultMessage: "Search by name or email",
  },
  noOptsMess: {
    id: "inputs.user-typeahead.noOptsMess",
    defaultMessage: "No results found",
  },
  noOptions: {
    id: "inputs.user-typeahead.noOptions",
    defaultMessage: "No results found",
  },
});
